/**
 * SurchargeFunctions.js — Moteur de Majorations Tarifaires v1.6.0
 *
 * Ce module gère l'évaluation et l'application des majorations tarifaires
 * (surcharges) basées sur des dates spécifiques, jours de la semaine,
 * plages horaires, ou combinaisons de ces critères.
 *
 * Toutes les évaluations de date/heure utilisent le fuseau Europe/Paris.
 *
 * Règles de cumul :
 * - Si une règle non-cumulable (stackable=false) correspond → elle seule est appliquée
 *   (celle avec la priorité la plus haute si plusieurs non-cumulables matchent)
 * - Sinon → toutes les règles cumulables (stackable=true) sont appliquées
 * - Ordre d'application : montants fixes d'abord, puis pourcentages sur le prix de BASE
 *
 * @module SurchargeFunctions
 */

import { getParisDateString, getParisDayOfWeek } from './DateFunctions';

// ─────────────────────────────────────────────
// 1. PARIS TIMEZONE HELPERS
// ─────────────────────────────────────────────

/**
 * Extrait l'heure au format "HH:MM" selon un fuseau horaire donné (défaut Europe/Paris).
 * @param {string|Date|number} dateInput - Date à évaluer
 * @param {string} [tz='Europe/Paris'] - Fuseau horaire
 * @returns {string|null} "HH:MM" ou null si invalide
 */
export const getParisTimeString = (dateInput, tz = 'Europe/Paris') => {
  if (!dateInput) return null;

  // Handle "HH:MM" strings directly
  if (typeof dateInput === 'string') {
    const raw = dateInput.trim();
    if (/^\d{2}:\d{2}$/.test(raw)) {
      return raw;
    }
  }

  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return null;

  try {
    const targetTz = (tz && typeof tz === 'string' && tz.trim()) ? tz.trim() : 'Europe/Paris';
    const formatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: targetTz,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return formatter.format(d);
  } catch (e) {
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${hours}:${mins}`;
  }
};

export const getLocalDateString = getParisDateString;
export const getLocalDayOfWeek = getParisDayOfWeek;
export const getLocalTimeString = getParisTimeString;

/**
 * Vérifie si une heure "HH:MM" tombe dans une plage horaire.
 * Gère les plages traversant minuit (ex: 22:00 → 06:00).
 *
 * @param {string} time - Heure à tester "HH:MM"
 * @param {string} startTime - Début de la plage "HH:MM"
 * @param {string} endTime - Fin de la plage "HH:MM"
 * @returns {boolean}
 */
export const isTimeInRange = (time, startTime, endTime) => {
  if (!time || !startTime || !endTime) return false;

  // Normalize to comparable format
  const t = time.replace(':', '');
  const s = startTime.replace(':', '');
  const e = endTime.replace(':', '');

  if (s <= e) {
    // Normal range: 08:00 → 18:00
    return t >= s && t < e;
  } else {
    // Midnight-crossing range: 22:00 → 06:00
    return t >= s || t < e;
  }
};

// ─────────────────────────────────────────────
// 2. SURCHARGE APPLICABILITY
// ─────────────────────────────────────────────

/**
 * Vérifie si une majoration individuelle s'applique à un trajet donné.
 *
 * @param {object} surcharge - La règle de majoration
 * @param {string|Date} scheduledAt - Date/heure du trajet
 * @param {string} vehicleId - ID du véhicule sélectionné
 * @returns {{ applicable: boolean, reason?: string }}
 */
export const isSurchargeApplicable = (surcharge, scheduledAt, vehicleId, tz = 'Europe/Paris') => {
  // 0. Must be enabled
  if (!surcharge || !surcharge.enabled) {
    return { applicable: false };
  }

  // 1. Vehicle check
  const vehicleIds = Array.isArray(surcharge.vehicleIds) ? surcharge.vehicleIds : [];
  if (vehicleIds.length > 0 && !vehicleIds.includes('ALL')) {
    if (!vehicleId || !vehicleIds.includes(vehicleId)) {
      return { applicable: false };
    }
  }

  // Extract date, day, and time using instance timezone
  const dateStr = getParisDateString(scheduledAt, tz);
  const dayOfWeek = getParisDayOfWeek(scheduledAt, tz);
  const timeStr = getParisTimeString(scheduledAt, tz);

  if (!dateStr) return { applicable: false };

  const type = surcharge.type;

  // 2. Evaluate by type
  switch (type) {
    case 'specific_dates': {
      const dates = Array.isArray(surcharge.specificDates) ? surcharge.specificDates : [];
      if (!dates.includes(dateStr)) {
        return { applicable: false };
      }
      return { applicable: true, reason: 'specific_date' };
    }

    case 'weekday': {
      const weekdays = Array.isArray(surcharge.weekdays) ? surcharge.weekdays : [];
      if (dayOfWeek === null || !weekdays.includes(dayOfWeek)) {
        return { applicable: false };
      }
      return { applicable: true, reason: 'weekday' };
    }

    case 'time': {
      if (!timeStr || !surcharge.startTime || !surcharge.endTime) {
        return { applicable: false };
      }
      if (!isTimeInRange(timeStr, surcharge.startTime, surcharge.endTime)) {
        return { applicable: false };
      }
      return { applicable: true, reason: 'time_range' };
    }

    case 'weekday_time': {
      const weekdays = Array.isArray(surcharge.weekdays) ? surcharge.weekdays : [];
      if (dayOfWeek === null || !weekdays.includes(dayOfWeek)) {
        return { applicable: false };
      }
      if (!timeStr || !surcharge.startTime || !surcharge.endTime) {
        return { applicable: false };
      }
      if (!isTimeInRange(timeStr, surcharge.startTime, surcharge.endTime)) {
        return { applicable: false };
      }
      return { applicable: true, reason: 'weekday_time' };
    }

    case 'specific_dates_time': {
      const dates = Array.isArray(surcharge.specificDates) ? surcharge.specificDates : [];
      if (!dates.includes(dateStr)) {
        return { applicable: false };
      }
      if (!timeStr || !surcharge.startTime || !surcharge.endTime) {
        // If no time range specified, match date only
        return { applicable: true, reason: 'specific_date' };
      }
      if (!isTimeInRange(timeStr, surcharge.startTime, surcharge.endTime)) {
        return { applicable: false };
      }
      return { applicable: true, reason: 'specific_date_time' };
    }

    default:
      return { applicable: false };
  }
};

// ─────────────────────────────────────────────
// 3. SURCHARGE FILTERING
// ─────────────────────────────────────────────

/**
 * Filtre et retourne toutes les majorations applicables pour un trajet,
 * en respectant les règles de cumul et de priorité.
 *
 * @param {Array} surcharges - Liste de toutes les majorations configurées
 * @param {string|Date} scheduledAt - Date/heure du trajet
 * @param {string} vehicleId - ID du véhicule
 * @returns {Array} Majorations applicables (après résolution cumul/priorité)
 */
export const getApplicableSurcharges = (surcharges, scheduledAt, vehicleId, tz = 'Europe/Paris') => {
  if (!Array.isArray(surcharges) || surcharges.length === 0) return [];
  if (!scheduledAt) return [];

  // Step 1: Find all matching surcharges
  const matching = [];
  for (const surcharge of surcharges) {
    const result = isSurchargeApplicable(surcharge, scheduledAt, vehicleId, tz);
    if (result.applicable) {
      matching.push({ ...surcharge, _matchReason: result.reason });
    }
  }

  if (matching.length === 0) return [];

  // Step 2: Separate stackable and non-stackable
  const nonStackable = matching.filter((s) => s.stackable === false);
  const stackable = matching.filter((s) => s.stackable !== false);

  // Step 3: Apply priority/cumul rules
  if (nonStackable.length > 0) {
    // Non-stackable with highest priority wins alone
    nonStackable.sort((a, b) => (b.priority || 0) - (a.priority || 0));
    return [nonStackable[0]];
  }

  // All stackable → return all, sorted by priority (highest first)
  stackable.sort((a, b) => (b.priority || 0) - (a.priority || 0));
  return stackable;
};

// ─────────────────────────────────────────────
// 4. SURCHARGE CALCULATION
// ─────────────────────────────────────────────

/**
 * Calcule le montant total des majorations à appliquer.
 *
 * RÈGLE CRITIQUE : Les pourcentages sont TOUJOURS calculés sur le basePrice,
 * jamais sur un prix intermédiaire. Cela garantit la prévisibilité.
 *
 * Exemple: basePrice=100, Nuit +10€, Weekend +10%
 * → 100 + 10 + (100 × 0.10) = 120€ (PAS 121€)
 *
 * @param {Array} applicableSurcharges - Majorations filtrées
 * @param {number} basePrice - Prix de base du trajet (avant majorations)
 * @returns {{ totalFixed: number, totalPercentage: number, totalPercentageAmount: number, totalSurcharge: number, details: Array }}
 */
export const calculateSurchargeAmount = (applicableSurcharges, basePrice) => {
  if (!Array.isArray(applicableSurcharges) || applicableSurcharges.length === 0 || !basePrice) {
    return {
      totalFixed: 0,
      totalPercentage: 0,
      totalPercentageAmount: 0,
      totalSurcharge: 0,
      details: [],
    };
  }

  let totalFixed = 0;
  let totalPercentage = 0;
  const details = [];

  for (const surcharge of applicableSurcharges) {
    const pricingType = surcharge.pricingType || 'percentage';
    let fixedPart = 0;
    let percentPart = 0;
    let percentAmount = 0;

    if (pricingType === 'fixed' || pricingType === 'fixed_and_percentage') {
      fixedPart = parseFloat(surcharge.fixedAmount) || 0;
      totalFixed += fixedPart;
    }

    if (pricingType === 'percentage' || pricingType === 'fixed_and_percentage') {
      percentPart = parseFloat(surcharge.percentage) || 0;
      percentAmount = basePrice * (percentPart / 100);
      totalPercentage += percentPart;
    }

    const amount = fixedPart + percentAmount;

    details.push({
      id: surcharge.id,
      name: surcharge.name || surcharge.id,
      pricingType,
      fixedAmount: parseFloat(fixedPart.toFixed(2)),
      percentage: percentPart,
      amount: parseFloat(amount.toFixed(2)),
      reason: surcharge._matchReason || null,
    });
  }

  const totalPercentageAmount = basePrice * (totalPercentage / 100);
  const totalSurcharge = totalFixed + totalPercentageAmount;

  return {
    totalFixed: parseFloat(totalFixed.toFixed(2)),
    totalPercentage,
    totalPercentageAmount: parseFloat(totalPercentageAmount.toFixed(2)),
    totalSurcharge: parseFloat(totalSurcharge.toFixed(2)),
    details,
  };
};

// ─────────────────────────────────────────────
// 5. MAIN ENTRY POINT
// ─────────────────────────────────────────────

/**
 * Point d'entrée principal : évalue et applique les majorations pour un trajet.
 *
 * @param {object} settings - Paramètres globaux (contenant settings.surcharges[])
 * @param {string|Date} scheduledAt - Date/heure prévue du trajet
 * @param {string} vehicleId - ID du véhicule sélectionné
 * @param {number} basePrice - Prix de base calculé par le moteur de pricing
 * @param {string} [customTz=null] - Fuseau horaire métier optionnel
 * @returns {{ finalPrice: number, surchargeDetails: Array, totalSurcharge: number, hasSurcharges: boolean }}
 */
export const applySurcharges = (settings, scheduledAt, vehicleId, basePrice, customTz = null) => {
  // Guard: no settings or no surcharges configured
  let surcharges = settings?.surcharges;
  if (!Array.isArray(surcharges) || surcharges.length === 0) {
    surcharges = settings?.globalRates?.surcharges;
  }
  if (!Array.isArray(surcharges) || surcharges.length === 0 || !scheduledAt || basePrice <= 0) {
    return {
      finalPrice: basePrice || 0,
      surchargeDetails: [],
      totalSurcharge: 0,
      hasSurcharges: false,
    };
  }

  const tz = customTz || settings?.timezone || settings?.timeZone || settings?.globalRates?.timezone || 'Europe/Paris';

  // Step 1: Get applicable surcharges
  const applicable = getApplicableSurcharges(surcharges, scheduledAt, vehicleId, tz);

  if (applicable.length === 0) {
    return {
      finalPrice: basePrice,
      surchargeDetails: [],
      totalSurcharge: 0,
      hasSurcharges: false,
    };
  }

  // Step 2: Calculate amounts
  const result = calculateSurchargeAmount(applicable, basePrice);

  // Step 3: Return final price
  const finalPrice = parseFloat((basePrice + result.totalSurcharge).toFixed(2));

  return {
    finalPrice,
    surchargeDetails: result.details,
    totalSurcharge: result.totalSurcharge,
    hasSurcharges: true,
  };
};
